"use client";

import Image from "./image";
import Link from "./router-link";
import { ArrowLeft, ArrowRight, ArrowUpRight, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { solutionCards } from "../lib/site-data";

const questions = [
  {
    label: "企业类型",
    prompt: "哪种描述更接近你的业务？",
    options: [
      ["brand", "国内品牌或专业服务"],
      ["manufacturing", "制造或工业品企业"],
      ["trade", "外贸或出口企业"],
    ],
  },
  {
    label: "目标市场",
    prompt: "增长主要面向哪个市场？",
    options: [
      ["domestic", "中国市场"],
      ["overseas", "海外市场"],
      ["both", "国内和海外都要做"],
    ],
  },
  {
    label: "当前短板",
    prompt: "现在最影响客户理解和咨询的是什么？",
    options: [
      ["expression", "产品价值讲不清"],
      ["website", "官网或独立站不好用"],
      ["acquisition", "搜索和询盘基础薄弱"],
    ],
  },
];

export function SolutionAdvisor() {
  const [answers, setAnswers] = useState(["brand", "domestic", "expression"]);
  const [activeStep, setActiveStep] = useState(0);
  const solutionIndex =
    answers[0] === "manufacturing"
      ? 4
      : answers[1] !== "domestic" && answers[2] === "website"
        ? 3
        : answers[1] !== "domestic" || answers[0] === "trade"
          ? 1
          : answers[2] === "website"
            ? 2
            : 0;
  const solution = solutionCards[solutionIndex];
  const updateAnswer = (questionIndex: number, value: string) => {
    setAnswers((current) =>
      current.map((answer, index) =>
        index === questionIndex ? value : answer,
      ),
    );
    if (questionIndex < questions.length - 1) setActiveStep(questionIndex + 1);
  };

  return (
    <div className="solution-advisor">
      <div className="advisor-controls">
        <div aria-label="诊断步骤" className="advisor-step-nav">
          {questions.map((question, index) => (
            <button
              aria-current={activeStep === index ? "step" : undefined}
              key={question.label}
              onClick={() => setActiveStep(index)}
              type="button"
            >
              <span>0{index + 1}</span>
              <strong>{question.label}</strong>
              {activeStep === index && (
                <motion.i layoutId="advisor-step-indicator" />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence initial={false} mode="wait">
          <motion.div
            animate={{ opacity: 1, x: 0 }}
            className="advisor-question-stage"
            exit={{ opacity: 0, x: -18 }}
            initial={{ opacity: 0, x: 24 }}
            key={activeStep}
            transition={{ type: "spring", stiffness: 190, damping: 24 }}
          >
            <span>第 0{activeStep + 1} 步</span>
            <h3>{questions[activeStep].prompt}</h3>
            <div className="advisor-option-grid">
              {questions[activeStep].options.map(([value, label], index) => (
                <button
                  aria-pressed={answers[activeStep] === value}
                  key={value}
                  onClick={() => updateAnswer(activeStep, value)}
                  type="button"
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{label}</strong>
                  <ArrowRight size={16} />
                  {answers[activeStep] === value && (
                    <motion.i layoutId={`advisor-option-${activeStep}`} />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="advisor-navigation">
          {activeStep > 0 && (
            <button
              onClick={() => setActiveStep((step) => step - 1)}
              type="button"
            >
              <ArrowLeft size={16} />
              上一步
            </button>
          )}
          {activeStep < questions.length - 1 ? (
            <button
              onClick={() => setActiveStep((step) => step + 1)}
              type="button"
            >
              下一步
              <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={() => setActiveStep(0)} type="button">
              <RotateCcw size={15} />
              重新选择
            </button>
          )}
        </div>
      </div>

      <div className="advisor-visual" aria-live="polite">
        <AnimatePresence initial={false}>
          <motion.article
            animate={{ clipPath: "inset(0 0 0% 0)", scale: 1 }}
            className="advisor-recommendation"
            exit={{ clipPath: "inset(0 0 100% 0)", scale: 1.03 }}
            initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.04 }}
            key={solution.number}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <Image
              src={solution.image}
              alt=""
              fill
              sizes="(max-width: 900px) 100vw, 48vw"
            />
            <div className="advisor-recommendation-copy">
              <span>{solution.number} / 推荐路径</span>
              <h3>{solution.title}</h3>
              <p>{solution.description}</p>
              <div>
                {solution.tags.map((tag) => (
                  <small key={tag}>{tag}</small>
                ))}
              </div>
              <nav>
                <Link
                  className="text-link"
                  href={`/solutions/${solution.slug}`}
                >
                  查看推荐方案 <ArrowUpRight size={16} />
                </Link>
                <Link className="text-link" href="/contact">
                  预约增长诊断 <ArrowUpRight size={16} />
                </Link>
              </nav>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>
    </div>
  );
}
